import { cloneDeep } from 'lodash'
import { Maybe, TowingRecordPaymentType, TowingRecordRouteType, CustomerType } from '../../../../common/types'
import { JobItemInput } from '../../../../components/TowingRecordItems/types'
import {
  toScalarBusinessId,
  toScalarEmail,
  toScalarPersonalIdentityCode,
  toScalarPhone,
  toScalarShortDate,
  toScalarTime,
} from '../../../../util/convertToScalar'
import { CustomerFormValues } from '../../../Customer/types'
import { getIndexOfRoute } from '../../components/routes/getIndexOfRoute'
import { AutomobileAndTouringClubFinlandInput, JobDetailsInput } from '../../towingRecordInput/towingRecordInputTypes'
import {
  AutomobileAndTouringClubFinlandInputMutation,
  BillingInputMutation,
  JobDetailsInputMutation,
  JobItemMutation,
  OrdererInputMutation,
  TowingRecordInput,
  TowingRecordRoute,
  TowingRecordRouteInputMutation,
} from '../../towingRecordInput/types'
import { vatMapping } from '../../../../util/vatCalculations'
import { AddTowingRecordFormValues } from '../../AddTowingRecord/types'
import { BillingInput } from '../../components/billing/TowingRecordBilling/types'
import { toALScalarTimestamp } from '../../components/routes/subforms/ArrivalRoute/mutation/toMutationVariables'
import { toSosServiceOrderMutationVariables } from '../../../../components/SosSubForm/mutation/toMutationVariables'

export const toNewTowingRecordInput = (inputValues: AddTowingRecordFormValues): TowingRecordInput => {
  const values = cloneDeep(inputValues)
  const allJobItems = values.jobItems.map(jobItem => jobItemstoMutationVariables(jobItem, values.itemsIncludeVat))
  if (inputValues.discountItem) {
    allJobItems.push(jobItemstoMutationVariables(inputValues.discountItem, values.itemsIncludeVat))
  }

  return {
    billing: billingtoMutationVariables(values.billing, values.paymentType),
    itemsIncludeVat: values.itemsIncludeVat,
    jobDetails: jobDetailsToMutationVariables(values.jobDetails),
    jobItems: allJobItems,
    stationId: values.stationId,
    operatorId: values.operatorId,
    vehicleInfoCheckId: values.vehicleInfoCheckId ? values.vehicleInfoCheckId : null,
    label: values.label,
    onCall: values.onCall,
    orderer: values.orderer ? customerToMutationVariables(values.orderer) : null,
    paymentType: values.paymentType,
    recordNumber: values.recordNumber,
    routes: values.routes.map(r => routeToMutationVariables(r)),
    signature: values.signature,
    tags: values.tags,
    type: values.type,
    vehicleDetails: {
      vehicleClass: values.vehicleDetails.vehicleClass,
      insuranceCompany: values.vehicleDetails.insuranceCompany,
      makeAndModel: values.vehicleDetails.makeAndModel,
      registrationNumber: values.vehicleDetails.registrationNumber,
      owner: values.vehicleDetails.owner,
      holder: values.vehicleDetails.holder,
    },
    automobileAndTouringClubFinland: values.automobileAndTouringClubFinland
      ? ALToMutationVariables(values.automobileAndTouringClubFinland, values.routes)
      : null,
    sosServiceOrder: values.sosServiceOrder ? toSosServiceOrderMutationVariables(values.sosServiceOrder) : null,
    lastModified: '',
    notificationMethod: null,
  }
}

export const jobItemstoMutationVariables = (jobItem: JobItemInput, itemsIncludeVat: boolean): JobItemMutation => {
  return {
    id: jobItem.id,
    itemId: jobItem.itemId,
    type: jobItem.type,
    name: jobItem.name,
    description: jobItem.description,
    quantity: jobItem.quantity,
    unitPrice: jobItem.unitPrice,
    unitPriceHasVatIncluded: itemsIncludeVat,
    unit: jobItem.unit,
    // set the vat amount based on the vat category, eg "standard" => 24
    vat: jobItem.vatCategory ? vatMapping[jobItem.vatCategory].vat : 0,
    discountType: jobItem.discountType,
    discount: jobItem.discount,
    netAmount: jobItem.netAmount,
    grossAmount: jobItem.grossAmount,
    vatAmount: jobItem.vatAmount,
    vatUnit: '1',
    vatCategory: jobItem.vatCategory,
    limitedUsage: jobItem.limitedUsage,
  }
}

const jobDetailsToMutationVariables = (jobDetails: JobDetailsInput): JobDetailsInputMutation => {
  return {
    serviceType: jobDetails.serviceType,
    orderReference: jobDetails.orderReference,
    towingDate: jobDetails.towingDate ? toScalarShortDate(jobDetails.towingDate) : null,
    towingReason: jobDetails.towingReason,
    additionalInfo: jobDetails.additionalInfo,
    notes: jobDetails.notes,
    incidentCaseNo: jobDetails.incidentCaseNo,
  }
}

const routeToMutationVariables = (route: TowingRecordRoute): TowingRecordRouteInputMutation => {
  return {
    id: route.id,
    ordering: route.ordering,
    address: route.address,
    city: route.city,
    zipcode: route.zipcode,
    coords: {
      lat: route.coords.lat,
      long: route.coords.long,
    },
    arrivalDate: route.arrivalDate ? toScalarShortDate(route.arrivalDate) : null,
    arrivalTime: route.arrivalTime ? toScalarTime(route.arrivalTime) : null,
    finishedDate: route.finishedDate ? toScalarShortDate(route.finishedDate) : null,
    finishedTime: route.finishedTime ? toScalarTime(route.finishedTime) : null,
    description: route.description,
    displayTimestampOnPrintables: route.displayTimestampOnPrintables,
    displayFinishedTimestampOnPrintables: route.displayTimestampOnPrintables,
    type: route.type,
  }
}

const billingtoMutationVariables = (
  billing: BillingInput,
  paymentType: Maybe<TowingRecordPaymentType>
): BillingInputMutation | null => {
  if (!billing) {
    return null
  }

  if (!paymentType) {
    return null
  }

  if (paymentType === TowingRecordPaymentType.define_later) {
    return null
  }

  return {
    type: paymentType,
    paymentDetails: {
      debtorName: billing.paymentDetails.debtorName,
      receiptEmail: billing.paymentDetails.receiptEmail ? toScalarEmail(billing.paymentDetails.receiptEmail) : null,
      paymentDate: toScalarShortDate(billing.paymentDetails.paymentDate),
    },
  }
}

const customerToMutationVariables = (customer: CustomerFormValues): OrdererInputMutation | null => {
  if (customer.id) {
    return {
      id: customer.id,
      personCustomer: null,
      companyCustomer: null,
    }
  }

  if (customer.personCustomer) {
    const { personCustomer } = customer
    return {
      id: null,
      personCustomer: {
        type: CustomerType.person,
        ssn: personCustomer.ssn ? toScalarPersonalIdentityCode(personCustomer.ssn) : null,
        firstName: personCustomer.firstName,
        lastName: personCustomer.lastName,
        email: personCustomer.email === '' ? null : toScalarEmail(personCustomer.email),
        phone: personCustomer.phone === '' ? null : toScalarPhone(personCustomer.phone),
        source: personCustomer.source
      },
      companyCustomer: null,
    }
  }

  if (customer.companyCustomer) {
    const { companyCustomer } = customer
    return {
      id: null,
      personCustomer: null,
      companyCustomer: {
        type: CustomerType.company,
        businessId: companyCustomer.businessId === '' ? null : toScalarBusinessId(companyCustomer.businessId),
        companyName: companyCustomer.companyName,
        contactPerson: companyCustomer.contactPerson,
        contactPersonEmail:
          companyCustomer.contactPersonEmail === '' ? null : toScalarEmail(companyCustomer.contactPersonEmail),
        phone: companyCustomer.phone === '' ? null : toScalarPhone(companyCustomer.phone),
        source: companyCustomer.source
      },
    }
  }

  return null
}

// Autoliitto
const ALToMutationVariables = (
  al: AutomobileAndTouringClubFinlandInput,
  routes: TowingRecordRoute[]
): AutomobileAndTouringClubFinlandInputMutation => {
  const arrivalRouteIndex = getIndexOfRoute(routes, TowingRecordRouteType.arrival)
  // finish route date and time = Autoliitto completion timestamp
  const finishRouteIndex = getIndexOfRoute(routes, TowingRecordRouteType.finish)
  const actualTimeOfArrival = { date: routes[arrivalRouteIndex].arrivalDate, time: routes[arrivalRouteIndex].arrivalTime }
  const actualTimeOfCompletion = { date: routes[finishRouteIndex].arrivalDate, time: routes[finishRouteIndex].arrivalTime }
  const estimatedTimeOfArrival = al.jobTimestamps.estimatedTimeOfArrival
  const estimatedTimeOfCompletion = al.jobTimestamps.estimatedTimeOfCompletion
  return {
    arcCodes: {
      completion: {
        code: al.arcCodes.completion ? al.arcCodes.completion.code : null,
        label: al.arcCodes.completion ? al.arcCodes.completion.label : null,
      },
      description: {
        code: al.arcCodes.description ? al.arcCodes.description.code : null,
        label: al.arcCodes.description ? al.arcCodes.description.label : null,
      },
      component: {
        code: al.arcCodes.component ? al.arcCodes.component.code : null,
        label: al.arcCodes.component ? al.arcCodes.component.label : null,
      },
    },
    caseNumber: al.caseNumber,
    delayedTowing: al.delayedTowing,
    delayedTowingReason: al.delayedTowingReason,
    distanceTowed: al.distanceTowed,
    jobId: al.jobId,
    personCount: al.personCount,
    repairShopId: al.repairShopId,
    serviceChannel: al.serviceChannel,
    towingInsistedByCustomer: al.towingInsistedByCustomer,
    vehicleMileage: al.vehicleMileage,
    jobTimestamps: {
      actualTimeOfArrival: actualTimeOfArrival ? toALScalarTimestamp(actualTimeOfArrival) : null,
      actualTimeOfCompletion: actualTimeOfCompletion ? toALScalarTimestamp(actualTimeOfCompletion) : null,
      estimatedTimeOfArrival: estimatedTimeOfArrival ? toALScalarTimestamp(estimatedTimeOfArrival) : null,
      estimatedTimeOfCompletion: estimatedTimeOfCompletion ? toALScalarTimestamp(estimatedTimeOfCompletion) : null,
    },
    billingInfo: al.billingInfo,
  }
}
